import { Injectable } from "@angular/core";
import { ApiService } from "src/app/core/backend/api.service";
import { SopoLoadingService } from "../sopo-loading/sopo-loading.service";
import { Device } from '@capacitor/device';
import { doc, docData, Firestore, getDocFromServer } from "@angular/fire/firestore";
import { BehaviorSubject, filter, firstValueFrom, map, Subscription } from "rxjs";
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { CryptoChainOptions, CryptoCurrencyTypes, IApprovedUserInfo, ICoinflowGetWithdrawerResponse, SopoWalletTransactionSubType, WithdrawOption, WithdrawSpeedType } from "@soposopo/sopo-models";
import { SopoAlertSettings } from "src/app/sopo-popup/interfaces/alert";
import { ProfileService } from "src/app/core/firebase/profile.service";
import { GoldIds, GOLDS } from "@soposopo/sopo-models/dist/shop/Golds";
import { PurchaseAnimationService, ShopAnimationPartToPlay } from "../purchase-animation/purchase-animation.service";
import { FeatureService } from "src/app/core/firebase/feature.service";
import { ShopService } from "src/app/core/firebase/shop.service";
import { isAddress } from 'viem'
import AppLauncherElectron from "src/app/plugins/app-launcher.plugin";
import { ProfileStateDispatcher } from "src/app/core/state/profile/profile.dispatcher";
import { isElectron } from "../../utils/platform.utils";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root'
})
export class CoinflowService {
  private _sopoWalletBalance: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private _sopoWalletBalanceSubscription: Subscription;
  private _userId: string;
  private _methods: ICoinflowMethods;
  private _coinflowGetWithdrawerError: { header?: string, message: string, verificationLink?: string };
  private _minWithdrawAmount: number;
  private _sopoShopProductPrice: BehaviorSubject<{ [key in GoldIds]: number } | null> = new BehaviorSubject(null);

  get sopoWalletBalance() {
    return this._sopoWalletBalance.asObservable();
  }

  get minWithdrawAmount() {
    return this._minWithdrawAmount;
  }

  get sopoShopProductPrice() {
    return this._sopoShopProductPrice.value;
  }

  get sopoShopProductPrice$() {
    return this._sopoShopProductPrice.asObservable();
  }

  constructor(
    private apiService: ApiService,
    private sopoLoadingService: SopoLoadingService,
    private firestore: Firestore,
    private sopoPopupService: SopoPopupService,
    private profileService: ProfileService,
    private puchaseAnimationService: PurchaseAnimationService,
    private featureService: FeatureService,
    private shopService: ShopService,
    private profileDispatcher: ProfileStateDispatcher,
  ) { }

  public async initializeCoinflow(userId: string) {
    if (!this.featureService.sweepbucks) return;
    console.debug(`[CoinflowService] - initializeCoinflow`);

    if (this._sopoWalletBalanceSubscription) {
      this._sopoWalletBalanceSubscription.unsubscribe();
      this._sopoWalletBalance.next(0);
    }

    this._userId = userId;
    try {
      const docRef = doc(this.firestore, `sopoWalletBalance/${userId}`);
      const sopoWalletBalanceDocData = docData(docRef);
      if (sopoWalletBalanceDocData) {
        this._sopoWalletBalanceSubscription = sopoWalletBalanceDocData.pipe(
          filter(data => data && data.balance !== undefined),
          map(data => data.balance),
        ).subscribe(balance => {
          const centToDollar = balance / 100;
          this._sopoWalletBalance.next(centToDollar);
        });
      }

      this.getConfigs();
    } catch (error) {
      console.error('[CoinflowService]', error);
    }
  }

  public async getVerfiedUserData() {
    const docRef = doc(this.firestore, `kyc/${ this._userId }`);
    const data = await firstValueFrom(docData(docRef));
    return data.approvedUserInfo as IApprovedUserInfo;
  }

  public async getWithdrawer(): Promise<ICoinflowGetWithdrawerResponse | null> {
    if (this._coinflowGetWithdrawerError !== undefined) { // This check for "Too many request";
      this.openInfoAlert(this._coinflowGetWithdrawerError);
      return;
    }

    console.debug(`[CoinflowService] - getWithdrawer`);
    try {
      // The backend registers the user on Coinflow if necessary and returns the required data about the withdrawer.
      this._methods = await this.apiService.coinflowGetWithdrawer({ userId: this._userId });
      return this._methods;
    } catch(error) {
      console.error('[CoinflowService]', error.message ? error.message : error);

      this.getWithdawerErrorHandling(error);
      if (this._coinflowGetWithdrawerError) this.openInfoAlert(this._coinflowGetWithdrawerError);
      else this.openInfoAlert({ message: "Something went wrong. Please try again later." });
    }

    return null;
  }

  public async coinflowAddPaymentReceiver(): Promise<void> {
    console.debug(`[CoinflowService] - coinflowAddPaymentReceiver`);

    this.sopoLoadingService.present('api');
    try {
      const response = await this.apiService.coinflowAddPaymentReceiver({ userId: this._userId });

      if (response.link) {
        this.sopoLoadingService.dismiss('api');

        if (isElectron()) {
          await this.openWindowAndWaitForClose(response.link, { mode: 'receiver' }, 'coinflowAddReceiver');
        } else {
          this.openCheckoutPage(response.link, { mode: 'receiver' });
        }
        return;
      }
    } catch (error) {
      console.error('[CoinflowService]', error);
      this.sopoPopupService.showAlert('app', {
        header: 'Failed to add',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
      });
    } finally {
      this.sopoLoadingService.dismiss('api');
    }
  }

  public async coinflowDeleteWithdrawerAccount(type: WithdrawOption, token: string): Promise<boolean> {
    console.debug(`[CoinflowService] - coinflowDeleteWithdrawerAccount`);

    this.sopoLoadingService.present('api');
    try {
      const response = await this.apiService.coinflowDeleteWithdrawerAccount({ userId: this._userId, type, token });

      if (response.status) {
        this.sopoPopupService.showAlert('app', {
          message: 'Deleted successfully.',
          dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
        });
        return true; // get customer withdraw methods
      }
    } catch (error) {
      console.error('[CoinflowService]', error);
      this.sopoPopupService.showAlert('app', {
        header: 'Delete',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
      });
    } finally {
      this.sopoLoadingService.dismiss('api');
    }
    return false;
  }

  public async coinflowSendPayout(amount: number, account: string, speed: WithdrawSpeedType): Promise<boolean> {
    console.debug(`[CoinflowService] - coinflowSendPayout`);

    this.sopoLoadingService.present('api');
    let result = false;
    try {
      this.checkWithdrawAmount(amount);
      const fixedAmount = parseInt((amount * 100).toFixed(2));

      const response = await this.apiService.coinflowSendPayout({
        amount: fixedAmount,
        account, // card / bankAccount - token
        userId: this._userId,
        speed,
        withdrawType: speed === 'card' ? SopoWalletTransactionSubType.CARD : SopoWalletTransactionSubType.ACH,
        currencyType: 'USD',
      });

      if (response.status) {
        this.sopoPopupService.showAlert('app', {
          header: 'Withdraw Successful',
          message: 'Withdraw successfully, but this may take a little time to process.',
          dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
        });
      }

      result = true;
    } catch (error) {
      console.error('[CoinflowService]', error);
      this.sopoPopupService.showAlert('app', {
        header: 'Withdraw Failed',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
      });
    } finally {
      this.sopoLoadingService.dismiss('api');
      return result;
    }
  }

  public async coinflowDeposit(amount: number): Promise<void> {
    console.debug(`[CoinflowService] - coinflowDeposit - amount: ${amount}`);

    this.sopoLoadingService.present('api');
    try {
      const deviceId = (await  Device.getId()).identifier;
      const fixedAmount = parseInt((amount * 100).toFixed(2));

      const response = await this.apiService.coinflowDeposit({
        userId: this._userId,
        amount: fixedAmount,
        deviceId,
      })

      if (response.link) {
        this.openCheckoutPage(response.link, {
          mode: 'deposit',
          amount: amount.toString(),
        });
      }
    } catch (error) {
      console.error('[CoinflowService]', error);
      this.sopoPopupService.showAlert('app', {
        header: 'Deposit Failed',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
      });
      return Promise.reject();
    } finally {
      this.sopoLoadingService.dismiss('api');
    }
  }

    public async redeemSweepsbucks(amount: number, redeemAll = false): Promise<boolean> {
        console.debug(`[CoinflowService] - redeem - amount: ${amount} - redeemAll: ${redeemAll}`);

        this.sopoLoadingService.present('api');
        try {
            const wager = await firstValueFrom(this.profileDispatcher.getExperienceObservable().pipe(
                map(experienceData => {
                    return experienceData.totalWagesSB
                }),
            ));

            if (wager === 0) {
                throw { error: "You must play a Jackpot SNG or one hand in a cash game with your Sweeps Coins before redeeming." };
            }

            const userSweepsbucks = await firstValueFrom(this.profileService.getProfile(this._userId).pipe(
                map(profile => profile.sweepbucks),
            ));

            if (userSweepsbucks === 0) throw { error: "You have no Sweeps Coins to redeem." };

            if (!redeemAll && amount > userSweepsbucks) {
                throw { error: "Insufficient Sweeps Coins." };
            }

            await this.apiService.redeemSweepsbucks({
                userId: this._userId,
                amount: redeemAll ? userSweepsbucks : amount,
            });

            this.openInfoAlert({
                header: 'Redemption Successful',
                message: 'You have succesfully redeemed your selected amount of Sweeps Coins. It may take a moment for your balance to update.',
            });

            return true;
        } catch (error) {
            console.error('[CoinflowService]', error);
            this.openInfoAlert({
                header: 'Redeem Failed',
                message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.'
            })

            return false;
        } finally {
            this.sopoLoadingService.dismiss('api');
        }
    }

  public async sopoWalletPurchase(productId: GoldIds, product: typeof GOLDS[GoldIds], playAnimation: ShopAnimationPartToPlay): Promise<void> {
    console.debug(`[CoinflowService] - sopoWalletPurchase`);

    this.sopoLoadingService.present('api');

    try {
      const prices = this._sopoShopProductPrice.value;
      if (!prices) {
        throw { error: 'Product prices not loaded yet. Please try again.' };
      }

      const result = {
        amount: prices[productId],
        currency: 'USD',
      };

      if (this._sopoWalletBalance.value < result.amount || this._sopoWalletBalance.value === 0) throw { error: "Insufficient SopoWallet Balance." };

      const fixedAmount = parseInt((result.amount * 100).toFixed(2));
      const bonusSweepbuck = this.featureService.sweepbucks ? true : false;

      await this.apiService.sopoWalletPurchase({
        userId: this._userId,
        productId,
        amount: fixedAmount,
        currencyType: result.currency,
        bonusSweepbuck
      });
      this.sopoLoadingService.dismiss('api');

      await this.puchaseAnimationService.animation(productId, product, playAnimation);

      this.shopService.purchaseConfirmation();
    } catch (error) {
      this.sopoLoadingService.dismiss('api');
      console.error('[CoinflowService]', error);
      this.openInfoAlert({
        header: 'Purchase Failed',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.'
      })
    }
  }

  /**
   * Purchase via Coinflow webview
   * Opens a Coinflow checkout webview where user can pay with any supported method
   */
  public async coinflowWebviewPurchase(productId: GoldIds, product: typeof GOLDS[GoldIds], playAnimation: ShopAnimationPartToPlay): Promise<void> {
    console.debug(`[CoinflowService] - coinflowWebviewPurchase - productId: ${productId}`);

    this.sopoLoadingService.present('api');

    try {
      const deviceId = (await Device.getId()).identifier;

      // Get checkout link from backend (creates pending transaction)
      const response = await this.apiService.coinflowPurchaseCheckoutLink({
        userId: this._userId,
        productId,
        deviceId,
      });

      if (response.link) {
        this.sopoLoadingService.dismiss('api');

        // Build wrapper page URL with product info and open in system browser
        const prices = this._sopoShopProductPrice.value;
        const price = prices?.[productId] ? (prices[productId] % 1 === 0 ? `$${prices[productId]}` : `$${prices[productId].toFixed(2)}`) : '';
        const checkoutParams: Record<string, string> = {
          mode: 'purchase',
          productId,
          price,
          transactionId: response.transactionId,
        };
        const browserResult = await this.openWindowAndWaitForClose(response.link, checkoutParams);

        if (browserResult === 'deeplink') {
          this.sopoLoadingService.present('api');

          // Wait for webhook to process and credit user
          await this.waitForPurchaseCompletion(this._userId, response.transactionId);

          this.sopoLoadingService.dismiss('api');

          // Play animation and show confirmation
          await this.puchaseAnimationService.animation(productId, product, playAnimation);
          this.shopService.purchaseConfirmation();
        } else {
          console.debug('[CoinflowService] - Browser closed without deeplink, not waiting for purchase completion');
        }
      } else {
        this.sopoLoadingService.dismiss('api');
      }
    } catch (error) {
      this.sopoLoadingService.dismiss('api');
      console.error('[CoinflowService]', error);
      this.openInfoAlert({
        header: 'Purchase Failed',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.'
      });
    }
  }

  /**
   * Wait for purchase completion by monitoring both sopoWalletTransactions and shop collection
   */
  private async waitForPurchaseCompletion(userId: string, transactionId: string): Promise<void> {
    console.debug(`[CoinflowService] - Waiting for purchase completion: ${transactionId}`);

    const { collection, collectionData, doc, docData, query, where } = await import('@angular/fire/firestore');
    const { filter, map, merge, timeout, catchError, of, firstValueFrom } = await import('rxjs');

    // Listen to sopoWalletTransactions doc for SUCCESS status (Coinflow purchases)
    const swtDocRef = doc(this.firestore, `sopoWalletTransactions/${transactionId}`);
    const swtObserver = docData(swtDocRef).pipe(
      filter((data: any) => data?.status === 'SUCCESS'),
      map(_ => 'SUCCESS')
    );

    // Listen to shop collection for doc with matching transactionId
    const shopCollectionRef = collection(this.firestore, `users/${userId}/shop`);
    const shopQueryRef = query(shopCollectionRef, where('transactionId', '==', transactionId));
    const shopObserver = collectionData(shopQueryRef).pipe(
      filter(docs => docs.length > 0),
      map(_ => 'SUCCESS')
    );

    // Race both observers - first one to emit SUCCESS wins
    const observer = merge(swtObserver, shopObserver).pipe(
      timeout(30 * 1000), // timeout after 30 sec
      catchError(_ => of('TIMEOUT'))
    );
    const result = await firstValueFrom(observer);

    if (result === 'TIMEOUT') {
      throw new Error('Purchase processing timed out. If you completed the payment, your items will be credited shortly.');
    }
  }

  public async cryptoWithdraw(amount: number, chain: CryptoChainOptions, walletAddress: string, currencyType: CryptoCurrencyTypes): Promise<boolean> {
    console.debug(`[CoinflowService] - cryptoWithdraw`);

    this.sopoLoadingService.present('api');
    let result = false;
    try {
      if (!this.walletAddressValidation(chain, walletAddress)) {
        throw { error: 'Invalid wallet address.' };
      }

      this.checkWithdrawAmount(amount);
      const fixedAmount = parseInt((amount * 100).toFixed(2));

      await this.apiService.cryptoWithdraw({
        userId: this._userId,
        amount: fixedAmount,
        chain,
        wallet: walletAddress,
        currencyType,
      });

      this.openInfoAlert({
        header: 'Withdraw Successful',
        message: 'Your withdraw request has been submitted. Please allow up to 72 hours to process.'
      })
      result = true;
    } catch (error) {
      console.error('[CoinflowService]', error);
      this.sopoPopupService.showAlert('app', {
        header: 'Withdraw Failed',
        message: error.error && typeof error.error === 'string' ? error.error : 'Something went wrong. Please try again later.',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
      })
    } finally {
      this.sopoLoadingService.dismiss('api');
      return result;
    }
  }

  private getWebAppUrl(): string {
    return environment.staging ? 'https://web.staging.playsopo.com' : 'https://app.playsopo.com';
  }

  private async openCheckoutPage(coinflowUrl: string, params: Record<string, string>): Promise<void> {
    console.debug(`[CoinflowService] - openCheckoutPage`);

    const appUrl = this.getWebAppUrl();
    const queryParams = new URLSearchParams({ url: coinflowUrl, ...params });
    const checkoutUrl = `${appUrl}/coinflow-checkout?${queryParams.toString()}`;

    if (isElectron()) {
      AppLauncherElectron.openUrl({ url: checkoutUrl });
    } else {
      window.open(checkoutUrl, '_system');
    }
  }

  /**
   * Opens the checkout wrapper page in the system browser and waits for deeplink callback or manual close.
   * Uses Electron's IPC deeplink handler and visibility change as fallback.
   */
  private openWindowAndWaitForClose(coinflowUrl: string, params: Record<string, string>, deeplinkSuccessKey = 'coinflowPurchase'): Promise<'deeplink' | 'closed'> {
    console.debug(`[CoinflowService] - openWindowAndWaitForClose`);

    return new Promise((resolve) => {
      let resolved = false;

      const done = (result: 'deeplink' | 'closed') => {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('visibilitychange', visibilityHandler);
        window.electronAPI?.offDeepLink(deepLinkHandler);
        resolve(result);
      };

      // Listen for deeplink callback from Electron main process
      const deepLinkHandler = (_event: unknown, deepLinkUrl: string) => {
        if (resolved) return;
        try {
          const parsedUrl = new URL(deepLinkUrl);
          if (parsedUrl.searchParams.get(deeplinkSuccessKey) === 'success') {
            done('deeplink');
          }
        } catch (e) {
          console.error('[CoinflowService] - Failed to parse deeplink URL', e);
          done('closed');
        }
      };

      window.electronAPI?.onDeepLink(deepLinkHandler);

      // Build checkout wrapper page URL and open in system browser
      const appUrl = this.getWebAppUrl();
      const queryParams = new URLSearchParams({ url: coinflowUrl, ...params });
      const checkoutUrl = `${appUrl}/coinflow-checkout?${queryParams.toString()}`;
      AppLauncherElectron.openUrl({ url: checkoutUrl });

      // Fallback: detect when user returns to app without deeplink
      const visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          // Small delay to allow deeplink IPC to be processed first
          setTimeout(() => done('closed'), 500);
        }
      };

      document.addEventListener('visibilitychange', visibilityHandler);
    });
  }

  private openInfoAlert(option: { header?: string, message: string, verificationLink?: string }): void {
    const sopoAlertSettings: SopoAlertSettings = {
        header: option.header,
        message: option.message,
        backdropDismiss: true,
        buttonText: 'Ok',
        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
    };

    if (option.verificationLink) {
        sopoAlertSettings.buttonHandler = () => {
            AppLauncherElectron.openUrl({ url: option.verificationLink });
        }
    }
    this.sopoPopupService.showAlert('app', sopoAlertSettings);
  }

  public logoutClear(): void {
    console.debug(`[CoinflowService] - logoutClear`);

    this._sopoWalletBalance.next(0);
    this._coinflowGetWithdrawerError = undefined;
  }

  private checkWithdrawAmount(amount: number): void {
    const min = this.minWithdrawAmount;
    const balance = this._sopoWalletBalance.value;

    if (balance < min) {
      throw {
        error: `Your SopoWallet balance is below the minimum withdrawal amount of $${min}.`
      };
    }
    if (amount > balance) {
      throw {
        error: `The amount you requested exceeds your available SopoWallet balance of $${balance}.`
      };
    }
  }

  private walletAddressValidation(chain: CryptoChainOptions, address: string): boolean {
    console.debug(`[CoinflowService] - walletAddressValidation`);

    if (chain === CryptoChainOptions.ETHEREUM) {
      return isAddress(address);
    } else if (chain === CryptoChainOptions.SOLANA) {
      const solanaRegex: RegExp = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaRegex.test(address);
    }

    return false;
  }

  private getWithdawerErrorHandling(error: any): void {
    switch (error.status) {
      case 422:
        this._coinflowGetWithdrawerError = {
          header: 'Unable to Verify Your Identity',
          message: 'We couldn\u2019t verify your identity due to invalid information. Please reach out to support to resolve it.'
        };
        break;
      case 451:
        this._coinflowGetWithdrawerError = {
            header: 'Unable to Verify Your Identity',
            message: error.error.message,
            verificationLink: error.error.verificationLink,
          };
      default:
        // No action needed for other status codes (yet)
    }
  }

  private async getConfigs(): Promise<void> {
    console.debug(`[CoinflowService] - getConfigs`);

    const docRef = doc(this.firestore, 'config/cash-config');
    const docFromServer = await getDocFromServer(docRef);

    if (!docFromServer.exists()) throw new Error('Config not found!');

    const data = docFromServer.data();

    if (data.sopoShopProductPrice === undefined || data.minWithdrawAmount === undefined) {
      throw new Error('Missing config value(s)!');
    }

    this._sopoShopProductPrice.next(data.sopoShopProductPrice);
    this._minWithdrawAmount = data.minWithdrawAmount;
  }
}

export interface ICoinflowMethods {
  bankAccounts: Array<IBankAccount>,
  cards: Array<ICard>,
}

interface ICard {
  last4: string,
  type: string,
  disbursementStatus: string,
  token: string,
  createdAt: string,
}

interface IBankAccount {
  alias: string,
  token: string,
  routingNumber: string,
  last4: string,
  accountHash: string,
  rtpEligible: boolean,
  reference: string,
}
